import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/App.css";
import "@/styles/premiumTheme.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// console.log("🌐 Environment Check:");
// console.log("  REACT_APP_BACKEND_URL:", process.env.REACT_APP_BACKEND_URL);
// console.log("  BACKEND_URL:", BACKEND_URL);
// console.log("  API baseURL:", API);

// Axios instance
const api = axios.create({
  baseURL: API,
  withCredentials: true
});

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Add JWT token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  // console.log("🔍 Axios Interceptor - Token from localStorage:", token ? `${token.substring(0, 20)}...` : "NULL");
  // console.log("🔍 Request URL:", config.url);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    // console.log("✓ Authorization header added");
  } 
  return config;
});

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      // Silently fail - user is not authenticated
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    // console.log("🔐 Starting login...");
    const response = await api.post("/auth/login", { email, password });
    // console.log("✅ Login response:", response.data);

    // Store token in localStorage if provided
    if (response.data.token) {
      // console.log("💾 Storing token in localStorage:", response.data.token);
      localStorage.setItem("auth_token", response.data.token);
      // console.log("✓ Token stored. Verification:", localStorage.getItem("auth_token"));
    } else {
      console.warn("⚠️ No token in response!");
    }

    setUser(response.data.user);
    // console.log("👤 User state updated:", response.data.user);
    return response.data;
  };

  const register = async (email, password, name, phone) => {
    const response = await api.post("/auth/register", {
      email,
      password,
      name,
      phone,
    });
    
    // Store token in localStorage if provided
    if (response.data.token) {
      localStorage.setItem("auth_token", response.data.token);
    }
    
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      // Ignore logout API errors (e.g., 401 if token already expired)
      // console.log("Logout API call failed, clearing local session anyway");
    } finally {
      // Always clear local session and token
      localStorage.removeItem("auth_token");
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, loading, login, register, logout, checkAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Auth Callback for Google OAuth
function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get("session_id");

      if (!sessionId) {
        navigate("/login");
        return;
      }

      try {
        const response = await api.post(
          "/auth/google/session",
          {},
          {
            headers: { "X-Session-ID": sessionId },
          },
        );
        
        // Store session token in localStorage if provided
        if (response.data.session_token) {
          localStorage.setItem("auth_token", response.data.session_token);
        }
        
        setUser(response.data.user);
        navigate("/dashboard", {
          state: { user: response.data.user },
          replace: true,
        });
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/login");
      }
    };

    processSession();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#0A1128] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div>
        <p className="mt-4 text-[#94A3B8]">Authenticating...</p>
      </div>
    </div>
  );
}

// Protected Route
function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A1128] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Import pages (will be created)
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import ApplicationPage from "./pages/ApplicationPage";
import DashboardPage from "./pages/DashboardPage";
import AssetsPage from "./pages/AssetsPage";
import AssetDetailPage from "./pages/AssetDetailPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminInvestors from "./pages/admin/AdminInvestors";
import AdminAssets from "./pages/admin/AdminAssets";
import AdminCommitments from "./pages/admin/AdminCommitments";
import AdminPayouts from "./pages/admin/AdminPayouts";
import NotFoundPage from "./pages/NotFoundPage";
import NotificationsPage from "./pages/NotificationsPage";

function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL fragment (Google OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/apply" element={<ApplicationPage />} />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />

      {/* Investor Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets"
        element={
          <ProtectedRoute>
            <AssetsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/:assetId"
        element={
          <ProtectedRoute>
            <AssetDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/investors"
        element={
          <ProtectedRoute adminOnly>
            <AdminInvestors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assets"
        element={
          <ProtectedRoute adminOnly>
            <AdminAssets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/commitments"
        element={
          <ProtectedRoute adminOnly>
            <AdminCommitments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payouts"
        element={
          <ProtectedRoute adminOnly>
            <AdminPayouts />
          </ProtectedRoute>
        }
      />

      {/* 404 Catch-All Route */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

import SlashEasyWidget from "./components/SlashEasyWidget";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <AppRouter />
            <SlashEasyWidget />
            <Toaster position="top-right" theme="dark" richColors />
          </QueryClientProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
export { api };
